// Utils
import { stringify } from "qs";
import { useEffect } from "react";
import { useHistory } from "react-router";
import {
    FILTER_ALERT,
    type FilterAlertTypeEnum,
    FROM,
    TO,
} from "src/components/Filters/FilterAlert/hooks/useFilterAlert.hooks";
import { useQueryString } from "src/design-system/core/hooks";
import { useRouteMetadata } from "src/hooks";
import { PATH_SPONSORED_ADS } from "src/routesConstants";
import type { ThirdServiceCategory } from "src/types/posCredential";
import { PageEnum } from "src/utils/tracking/types/pageEnum";

export const SPONSORED_ADS_TABLES_VIEW_QS_KEY = "sponsored-ads-table";
export const SPONSORED_AD_LIST_SPECIFIC_ID_QS_KEY = "sponsored-ad-ids";
export const SPONSORED_AD_LIST_FROM_MARKETING_FEEDBACK_QS_KEY = "from-marketing-feedback";
export const SPONSORED_ADS_ERROR_MODAL_QS_KEY = "sponsored-ads-error-modals";
export const SPONSORED_AD_GROUP_BY_QS_KEY = "sponsored-ad-group-by";
export const SPONSORED_AD_POS_QS_KEY = "sponsored-ad-pos";
export const SPONSORED_AD_SERVICE_QS_KEY = "sponsored-ad-service";
export const SPONSORED_AD_KPI_KEY_QS_KEY = "kpi-key";

export enum SponsoredAdsTablesTabsEnum {
    MANAGEMENT = "management",
    PERFORMANCE = "performance",
    DASHBOARD = "dashboard",
}

const sponsoredAdsTabToPage: Record<SponsoredAdsTablesTabsEnum, PageEnum> = {
    [SponsoredAdsTablesTabsEnum.MANAGEMENT]: PageEnum.SponsoredAdManagement,
    [SponsoredAdsTablesTabsEnum.DASHBOARD]: PageEnum.SponsoredAdDashboard,
    [SponsoredAdsTablesTabsEnum.PERFORMANCE]: PageEnum.SponsoredAdDetail,
};

export enum MarketingActionErrorsModalEnum {
    openingHour = "openingHour",
    invalidPos = "invalidPos",
    currentOffer = "currentOffer",
    onlineOfferSlot = "onlineOfferSlot",
    countSlot = "countSlot",
    missingProducts = "missingProducts",
    CashierSync = "cashierSync",
    invalidAudienceAdsManager = "invalidAudienceAdsManager",
    otherActionInCreation = "otherActionInCreation",
    parallelActionsDisabled = "parallelActionsDisabled",
}

interface SponsoredAdsCommonParams {
    tab?: SponsoredAdsTablesTabsEnum;
    sponsoredAdIds?: number[];
    filterAlert?: FilterAlertTypeEnum;
    groupBy?: string;
    kpiKey?: string;
    fromMarketingFeedback?: boolean;
}

interface SponsoredAdsBasicParams extends SponsoredAdsCommonParams {
    from?: string;
    to?: string;
}

interface SponsoredAdsExtendedParams extends SponsoredAdsBasicParams {
    baseKey?: string;
    compareKey?: string;
    pos?: number[];
    service?: ThirdServiceCategory[];
}

export const sponsoredAdsRoutes = {
    editView: ({
        tab,
        sponsoredAdId,
        duplicateSponsoredAdId,
    }: {
        tab?: MarketingActionErrorsModalEnum;
        sponsoredAdId?: number;
        duplicateSponsoredAdId?: number;
    } = {}): string =>
        `${PATH_SPONSORED_ADS}/edit/${sponsoredAdId}${stringify(
            {
                [SPONSORED_ADS_ERROR_MODAL_QS_KEY]: tab,
                duplicateSponsoredAdId: duplicateSponsoredAdId,
            },
            { addQueryPrefix: true }
        )}`,
    createView: ({ tab }: { tab?: MarketingActionErrorsModalEnum } = {}): string =>
        `${PATH_SPONSORED_ADS}/create${stringify(
            {
                [SPONSORED_ADS_ERROR_MODAL_QS_KEY]: tab,
            },
            { addQueryPrefix: true }
        )}`,
    tablesView: (params: SponsoredAdsBasicParams | SponsoredAdsExtendedParams = {}): string => {
        const { tab, sponsoredAdIds, filterAlert, groupBy, kpiKey, fromMarketingFeedback } = params;

        const queryParams: Record<string, unknown> = {
            [SPONSORED_ADS_TABLES_VIEW_QS_KEY]: tab,
            [SPONSORED_AD_LIST_SPECIFIC_ID_QS_KEY]: sponsoredAdIds,
            [FILTER_ALERT]: filterAlert,
            [SPONSORED_AD_GROUP_BY_QS_KEY]: groupBy,
            [SPONSORED_AD_KPI_KEY_QS_KEY]: kpiKey,
            [SPONSORED_AD_LIST_FROM_MARKETING_FEEDBACK_QS_KEY]:
                fromMarketingFeedback === true ? "1" : undefined,
        };

        if ("from" in params) {
            queryParams[FROM] = params.from;
            queryParams[TO] = params.to;
        }

        if ("baseKey" in params) {
            queryParams.baseKey = params.baseKey;
            queryParams.compareKey = params.compareKey;
            queryParams[SPONSORED_AD_POS_QS_KEY] = params.pos;
            queryParams[SPONSORED_AD_SERVICE_QS_KEY] = params.service;
        }

        return `${PATH_SPONSORED_ADS}${stringify(queryParams, { addQueryPrefix: true })}`;
    },
};

export function useSponsoredAdsTableViewTabs(defaultTab: SponsoredAdsTablesTabsEnum): {
    sponsoredAdsTableTab: SponsoredAdsTablesTabsEnum;
    setSponsoredAdsTableTab: (sponsoredAdsTableTab: SponsoredAdsTablesTabsEnum) => void;
} {
    const history = useHistory();
    const [sponsoredAdsTableTab] = useQueryString(SPONSORED_ADS_TABLES_VIEW_QS_KEY, defaultTab);
    const { updateMetadata } = useRouteMetadata();

    const setSponsoredAdsTableTab = (sponsoredAdsTableTab: SponsoredAdsTablesTabsEnum) =>
        history.push(sponsoredAdsRoutes.tablesView({ tab: sponsoredAdsTableTab }));

    useEffect(() => {
        const resolvedTab = sponsoredAdsTableTab ?? defaultTab;
        updateMetadata({
            page: sponsoredAdsTabToPage[resolvedTab] ?? PageEnum.SponsoredAdManagement,
        });
    }, [defaultTab, sponsoredAdsTableTab, updateMetadata]);

    return {
        sponsoredAdsTableTab: sponsoredAdsTableTab || defaultTab,
        setSponsoredAdsTableTab,
    };
}

export function useSponsoredErrorModals(): {
    marketingActionErrorsModal: MarketingActionErrorsModalEnum;
    setMarketingActionErrorsModal: (
        marketingActionErrorsModal: MarketingActionErrorsModalEnum
    ) => void;
} {
    const [marketingActionErrorsModal, setMarketingActionErrorsModal] = useQueryString(
        SPONSORED_ADS_ERROR_MODAL_QS_KEY,
        undefined
    );

    return { marketingActionErrorsModal, setMarketingActionErrorsModal };
}
